import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { uuid } from 'utils/uuid'

import { LoadingSpinner } from '@elo-kit/components/loading-spinner/LoadingSpinner'
import { useI18n } from '@elo-kit/components/i18n/i18n'
import { getElopageConfig } from 'utils/elopageConfig.utils'
import { Document, Page } from './react-pdf-components'

import { EmptyContent } from './EmptyContent'

export const DocumentContent = React.memo((props) => {
  const I18n = useI18n()
  const {
    fileUrl,
    fileName,
    classes,
    locales = {
      docIsNotVisibleTitle: I18n.t('react.shared.pdf_errors.title'),
      docIsNotVisibleContent: I18n.t('react.shared.pdf_errors.content'),
      docIsNotVisibleBtnContent: I18n.t('react.shared.pdf_errors.button'),
      renderPaginationInfo: (localeProps) => I18n.t('react.shared.num_of', localeProps),
    },
    containerHeight,
    previewMode,
    scale,
    pageNumber,
    onLoadError,
    onDocumentLoadProgress,
    onDocumentLoadSuccess,
  } = props
  const initialUrl = previewMode ? `${fileUrl}#${uuid()}` : fileUrl
  const [currentFileUrl, setCurrentFileUrl] = useState(initialUrl)

  useEffect(() => {
    if (previewMode) {
      setCurrentFileUrl(`${fileUrl}#${uuid()}`)
    }
  }, [fileName, previewMode])

  if (!fileUrl) {
    return <EmptyContent fileUrl={fileUrl} locales={locales} classes={classes} />
  }

  const env = getElopageConfig('env')

  return (
    <Document
      file={currentFileUrl}
      onLoadProgress={onDocumentLoadProgress}
      onLoadSuccess={(data) => onDocumentLoadSuccess(data)}
      onLoadError={onLoadError}
      noData={<EmptyContent fileUrl={fileUrl} locales={locales} classes={classes} />}
      error={<EmptyContent fileUrl={fileUrl} locales={locales} classes={classes} />}
      className={classes.pdfDocument}
      loading={<LoadingSpinner />}
      externalLinkTarget='_blank'
    >
      <Page
        className={classes.pdfPage}
        pageNumber={pageNumber}
        height={containerHeight}
        scale={scale / 100}
        onRenderError={(error) => {
          if (env !== 'production') {
            // eslint-disable-next-line
            console.log(error)
          } else {
            throw error
          }
        }}
      />
    </Document>
  )
})

DocumentContent.propTypes = {
  containerHeight: PropTypes.number.isRequired,
  scale: PropTypes.number.isRequired,
  fileUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  fileName: PropTypes.string,
  classes: PropTypes.object,
  locales: PropTypes.shape({
    docIsNotVisibleTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    docIsNotVisibleContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    docIsNotVisibleBtnContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    renderPaginationInfo: PropTypes.func,
  }),
  previewMode: PropTypes.bool,
  pageNumber: PropTypes.number,
  onLoadError: PropTypes.func,
  onDocumentLoadProgress: PropTypes.func,
  onDocumentLoadSuccess: PropTypes.func,
}

DocumentContent.defaultProps = {
  fileUrl: '',
  fileName: '',
  classes: {},
  previewMode: false,
  pageNumber: 1,
  onLoadError: () => {},
  onDocumentLoadProgress: () => {},
  onDocumentLoadSuccess: () => {},
}
